import {useCallback} from 'react';
import {Bar, BarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis} from 'recharts';
import styled from 'styled-components';
import {Typography} from '@/components/Typography';
import {StudentsCount} from '@/map/@types';
import {palette} from '@/styles';
import {HighlightBox, SectionTab, SectionTabs} from '../../StyledComponents';
import {STUDENT_TABS, STUDENT_TYPES} from '../constants';
import {chartWithComma} from '../utils';

export interface IProps {
  type: string;
  data: StudentsCount[];
  compareList: string[];
  tabIndex: number;
  onTabChange(index: number): void;
}

const labelOptions: {[key: string]: any} = {
  position: 'top',
  fontSize: 10,
  fontWeight: 500,
  formatter: chartWithComma,
};

export const StudentBarChart = ({
  data: rawdata,
  compareList,
  tabIndex,
  onTabChange,
}: IProps) => {
  const getChartData = useCallback(() => {
    const chartData = rawdata.map((students: StudentsCount) => {
      if (!students) return undefined;

      const chartData: Array<{name: string; value: number}> = STUDENT_TYPES.map(
        (name) => ({
          name,
          value: (students as any)[name] || 0,
        })
      );
      return chartData;
    });

    return chartData.map((item, index) => ({
      name: compareList[index],
      value: item?.[tabIndex]?.value || 0,
    }));
  }, [rawdata, compareList, tabIndex]);

  const chartData = getChartData();

  const getMaxData = () => {
    const maxValue = Math.max(...chartData.map((data) => data.value));

    if (maxValue === 0) return -1;

    return chartData.findIndex((item) => item.value === maxValue);
  };

  if (!rawdata) {
    return null;
  }

  const maxDataIndex = getMaxData();
  const countformat =
    tabIndex < STUDENT_TABS.findIndex((i) => i === '초등학교')
      ? '원아'
      : '학생';

  return (
    <>
      <SectionTabs>
        {STUDENT_TABS.map((i, idx) => (
          <SectionTab
            key={idx}
            active={idx === tabIndex}
            onClick={() => onTabChange(idx)}
          >
            {i}
          </SectionTab>
        ))}
      </SectionTabs>
      <ChartWrapper>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            barSize={32}
            margin={{
              top: 15,
            }}
          >
            <CartesianGrid
              vertical={false}
              stroke={palette.gray100}
              strokeWidth={1}
            />
            <XAxis
              dataKey="name"
              interval={0}
              tickLine={false}
              tick={{
                fill: palette.gray600,
                fontSize: 12,
              }}
            />
            <YAxis hide={true} />
            <Bar
              dataKey="value"
              fill={palette.primary400}
              label={labelOptions}
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartWrapper>

      <HighlightBox>
        <Typography variant="medium12">
          {maxDataIndex >= 0 ? (
            <>
              <strong>{STUDENT_TABS[tabIndex]}</strong> {countformat} 수는{' '}
              <span>
                <Typography color={palette.primary500} fontWeight="700">
                  {chartData[maxDataIndex].name}
                </Typography>
                이/가 가장 많아요.
              </span>
            </>
          ) : (
            `선택하신 지역에 ${STUDENT_TABS[tabIndex]} ${countformat} 데이터가 없습니다.`
          )}
        </Typography>
      </HighlightBox>
    </>
  );
};

const ChartWrapper = styled.div`
  height: 140px;
  tspan {
    fill: ${palette.gray600};
  }
`;
